/**
 * 数据转换工具
 * 处理API返回的数据格式，转换为前端需要的格式
 */

import { timestampToDate } from './protobufHelper';

/**
 * 处理探索卡片数据
 * @param {Object} card - API返回的探索卡片数据
 * @returns {Object} - 处理后的卡片数据
 */
export const processExplorationCard = (card) => {
  if (!card) return null;
  
  console.log("处理探索卡片数据:", card.id);
  
  // 字段名称映射：API字段 -> 前端期望字段
  let processedCard = {
    ...card,
    // 将API的steps字段映射为前端期望的step字段
    step: card.steps || card.step || [],
    // 将API的annotations字段映射为前端期望的annotation字段
    annotation: card.annotations || card.annotation || {
      result: [],
      qa: [],
      scenario: [],
      flow: []
    },
    // 保持flow_config字段不变，但确保存在
    flow_config: card.flow_config || null,
    // 确保title字段存在
    title: card.title || card.name || card.prompt || "未命名任务"
  };
  
  // 处理雷达图数据 - 优先从steps数组的score.dimension生成
  let radarData = [];
  if (card.chartData && Array.isArray(card.chartData.radar)) {
    // 直接使用已有的雷达图数据
    radarData = card.chartData.radar;
  } else if (processedCard.step && Array.isArray(processedCard.step)) {
    // 从step数组的score.dimension构建雷达图数据
    for (const step of processedCard.step) {
      if (step.score && Array.isArray(step.score)) {
        const scoreWithDimension = step.score.find(s => s.dimension && Array.isArray(s.dimension) && s.dimension.length > 0);
      if (scoreWithDimension) {
        radarData = scoreWithDimension.dimension.map(d => ({
            name: getDimensionName(d.latitude) || d.latitude || '未知维度',
            value: Math.round(d.score || (d.weight * 100) || 0), // 优先使用score，其次weight*100
            weight: d.weight || 0 // 保留原始权重值
        }));
          break; // 找到第一个有维度数据的就跳出
        }
      }
    }
  }
  
  // 处理折线图数据 - 根据step数组生成时间序列数据
  let lineData = [];
  if (card.chartData && Array.isArray(card.chartData.line)) {
    // 直接使用已有的折线图数据
    lineData = card.chartData.line;
  } else if (processedCard.step && Array.isArray(processedCard.step)) {
    // 从step构建折线图数据
    lineData = processedCard.step.map((s, index) => {
      let value = 0;
      // 尝试从score中获取数值
      if (s.score && Array.isArray(s.score) && s.score.length > 0) {
        const firstScore = s.score[0];
        if (firstScore.score && typeof firstScore.score === 'number') {
          value = Math.round(firstScore.score * 100); // 转换为百分比
        } else if (firstScore.confidence && typeof firstScore.confidence === 'number') {
          value = Math.round(firstScore.confidence * 100); // 使用置信度
        }
      }
      
      return {
        month: `步骤${index + 1}`, // 或者使用s.name
        value: value
      };
    });
  }
  
  // 处理时间戳
  const createdAt = card.inserted_at ? 
    (typeof card.inserted_at === 'string' ? new Date(card.inserted_at) : timestampToDate(card.inserted_at)) : 
    new Date();
  
  // 根据step数据生成模型评估数据
  const modelEvaluations = {};
  if (processedCard.step && Array.isArray(processedCard.step)) {
    // 遍历step数组，为每个步骤(即每个模型)生成评估数据
    processedCard.step.forEach((step, index) => {
      // 使用agent作为模型键名，转换为小写并去除空格
      const modelKey = step.agent ? step.agent.toLowerCase().replace(/\s+/g, '') : `model${index + 1}`;
      
      // 获取score数据
      const scoreData = step.score && Array.isArray(step.score) && step.score.length > 0 ? step.score[0] : null;
      
      // 创建评估数据对象
      modelEvaluations[modelKey] = {
        name: step.agent || `模型${index + 1}`,
        tags: ["AI模型", "评估"],
        description: scoreData?.description || scoreData?.reason || "无评估描述",
        score: scoreData?.score ? parseFloat(scoreData.score) * 100 : 0, // 转换为0-100分制
        scoreChange: "+0.0%",
        credibility: scoreData?.confidence ? Math.round(parseFloat(scoreData.confidence) * 100) : 0, // 转换为百分比
        credibilityChange: "+0.0%",
        updatedAt: step.updated_at ? 
          (typeof step.updated_at === 'string' ? new Date(step.updated_at).toLocaleString() : new Date(step.updated_at.seconds * 1000).toLocaleString()) : 
          new Date().toLocaleString(),
        updatedBy: "评估系统",
        strengths: [],
        weaknesses: []
      };
    });
  }
  
  // 确保有默认的评估数据
  if (Object.keys(modelEvaluations).length === 0) {
    modelEvaluations.defaultmodel = {
      name: "默认模型",
      tags: ["AI模型"],
      description: "无评估数据",
      score: 0,
      scoreChange: "+0.0%",
      credibility: 0,
      credibilityChange: "+0.0%",
      updatedAt: new Date().toLocaleString(),
      updatedBy: "系统",
      strengths: [],
      weaknesses: []
    };
  }
  
  // 更新processedCard的其他字段
  processedCard = {
    ...processedCard,
    inserted_at: createdAt,
    agents: card.agents || {
      overall: false,
      agent1: false,
      agent2: false,
    },
    chartData: {
      radar: radarData,
      line: lineData
    },
    changes: card.changes || [],
    credibilityChange: card.credibilityChange || "+0.0%",
    scoreChange: card.scoreChange || "+0.0%",
    // 添加模型评估数据
    modelEvaluations: modelEvaluations
  };
  
  // 确保必要的字段都存在
  if (!processedCard.summary && processedCard.response_summary) {
    // 如果没有summary，使用response_summary
    processedCard.summary = processedCard.response_summary;
  }
  
  if (!processedCard.author && processedCard.created_by) {
    // 如果没有author对象，根据created_by创建
    processedCard.author = {
      name: processedCard.created_by,
      avatar: null
    };
  }
  
  if (!processedCard.tags || !Array.isArray(processedCard.tags)) {
    // 使用keyword字段作为tags，如果没有则使用默认值
    processedCard.tags = card.keyword || ["未分类"];
  }
  
  // 处理scenario字段，确保数据结构正确
  if (processedCard.scenario && typeof processedCard.scenario === 'object') {
    // scenario字段已存在，保持不变
  } else {
    // 如果没有scenario，创建默认结构
    processedCard.scenario = {
      dimension_node: [],
      dimension_edge: []
    };
  }
  
  console.log("处理后的卡片数据:", processedCard.id);
  return processedCard;
};

// 辅助函数：根据维度ID获取维度名称
function getDimensionName(dimensionId) {
  const dimensionMap = {
    'd3000101-0101-0101-0101-000000000101': '准确率',
    'd3000102-0102-0102-0102-000000000102': '一致性',
    'd3000103-0103-0103-0103-000000000103': '完整性',
    'd3000029-0029-0029-0029-000000000029': '通用AI评估',
    'd3000030-0030-0030-0030-000000000030': '技术能力',
    'd3000031-0031-0031-0031-000000000031': '社会责任',
    'd3000032-0032-0032-0032-000000000032': '可靠性',
    'd3000033-0033-0033-0033-000000000033': '透明度',
    'd3000034-0034-0034-0034-000000000034': '可控性',
    'd3000035-0035-0035-0035-000000000035': '伦理问题',
    'd3000036-0036-0036-0036-000000000036': '隐私保护',
    'd3000037-0037-0037-0037-000000000037': '公平性'
  };
  
  return dimensionMap[dimensionId] || null;
}

/**
 * 处理任务卡片数据
 * @param {Object} task - API返回的任务卡片数据
 * @returns {Object} - 处理后的任务数据
 */
export const processTaskCard = (task) => {
  // 处理雷达图数据
  const radarData = task.step && task.step.length > 0 && task.step[task.step.length - 1].score 
    ? task.step[task.step.length - 1].score
        .filter(s => s.dimension && s.dimension.length > 0)
        .flatMap(s => s.dimension.map(d => ({
          name: d.latitude,
          value: d.weight * 100 // 转换为百分比值
        }))) 
    : [];
  
  // 处理折线图数据
  const lineData = task.step 
    ? task.step.map((s, index) => ({
        name: `步骤${index + 1}`,
        value: s.score && s.score.length > 0 
          ? parseFloat(s.score[0].score) * 100 
          : 0 // 使用第一个评分，转换为百分比值
      })) 
    : [];
  
  // 处理时间戳
  const createdAt = task.inserted_at ? timestampToDate(task.inserted_at) : new Date();
  
  // 确保templateData正确设置
  let processedTask = {
    ...task,
    inserted_at: createdAt,
    agents: task.agents || {
      overall: false,
      agent1: false,
      agent2: false,
    },
    chartData: {
      radar: radarData,
      line: lineData
    },
    changes: [],
    credibilityChange: "+0.0%",
    scoreChange: "+0.0%",
  };
  
  // 如果没有templateData字段但有step数据，可以尝试从中创建templateData
  if (!processedTask.templateData && processedTask.step) {
    if (Array.isArray(processedTask.step)) {
      // 保持原有的step数组结构，不做修改
    } else if (typeof processedTask.step === 'object') {
      // 如果step是一个对象而不是数组，尝试查找其中的templateData
      if (processedTask.step.templateData) {
        processedTask.templateData = processedTask.step.templateData;
      }
    }
  }
  
  return processedTask;
};

/**
 * 处理探索列表响应
 * @param {Object} response - API返回的探索列表响应
 * @returns {Object} - 处理后的响应数据
 */
export const processExplorationsResponse = (response) => {
  // 如果响应为空或无效，返回默认结构
  if (!response || !response.cards) {
    return {
      cards: [],
      pagination: {
        total: 0,
        page: 1,
        per_page: 10
      }
    };
  }
  
  // 处理卡片数据
  const processedCards = Array.isArray(response.cards) 
    ? response.cards.map(processExplorationCard)
    : [];
  
  return {
    cards: processedCards,
    pagination: response.pagination || {
      total: 0,
      page: 1,
      per_page: 10
    },
    filter_echo: response.filter_echo || null,
    sort_echo: response.sort_echo || null
  };
};

/**
 * 处理任务列表响应
 * @param {Object} response - API返回的任务列表响应
 * @returns {Object} - 处理后的响应数据
 */
export const processTasksResponse = (response) => {
  // 如果响应为空或无效，返回默认结构
  if (!response || !response.cards) {
    return {
      cards: [],
      pagination: {
        total: 0,
        page: 1,
        per_page: 10
      }
    };
  }
  
  // 处理卡片数据
  const processedCards = Array.isArray(response.cards) 
    ? response.cards.map(processTaskCard)
    : [];
  
  return {
    cards: processedCards,
    pagination: response.pagination || {
      total: 0,
      page: 1,
      per_page: 10
    },
    filter_echo: response.filter_echo || null,
    sort_echo: response.sort_echo || null
  };
}; 